import {
  GoogleSignin,
  statusCodes,
  type ConfigureParams,
} from '@react-native-google-signin/google-signin';

export type GoogleSignInResult = {
  success: true;
  idToken: string;
  user: {
    id: string;
    email: string | null;
    name: string | null;
    photo: string | null;
  };
} | {
  success: false;
  error: string;
  cancelled?: boolean;
};

/**
 * GoogleSignInManager handles Google OAuth authentication flow.
 * Encapsulates configuration, sign-in, sign-out, and error handling.
 */
export class GoogleSignInManager {
  private initialized = false;

  /**
   * Configure Google Sign-In with the provided client ID.
   * Must be called before any sign-in operations.
   */
  configure(clientId: string): void {
    if (this.initialized) {
      return;
    }

    const config: ConfigureParams = {
      webClientId: clientId,
      scopes: ['profile', 'email'],
    };

    GoogleSignin.configure(config);
    this.initialized = true;
  }

  /**
   * Check if a user is currently signed in to Google.
   */
  async isSignedIn(): Promise<boolean> {
    this.ensureInitialized();
    try {
      const currentUser = await GoogleSignin.getCurrentUser();
      return currentUser !== null;
    } catch {
      return false;
    }
  }

  /**
   * Initiate Google Sign-In flow and return authentication tokens.
   * Always prompts for account selection to allow switching accounts.
   */
  async signIn(): Promise<GoogleSignInResult> {
    this.ensureInitialized();

    try {
      await GoogleSignin.hasPlayServices();

      // Sign out first to force account picker to show
      // This allows users to switch between Google accounts
      try {
        await GoogleSignin.signOut();
      } catch (signOutError) {
        // Ignore sign out errors - user may not be signed in
        console.log('[GoogleSignInManager] Sign out before sign in (expected if not signed in)');
      }

      const response = await GoogleSignin.signIn();

      if (!response.data) {
        return {
          success: false,
          error: 'Something went wrong: No user data returned from Google Sign-In',
        };
      }

      const { user, idToken } = response.data;

      if (!idToken) {
        return {
          success: false,
          error: 'Something went wrong: No ID token received from Google',
        };
      }

      return {
        success: true,
        idToken,
        user: {
          id: user.id,
          email: user.email,
          name: user.name,
          photo: user.photo,
        },
      };
    } catch (error: unknown) {
      return this.handleError(error);
    }
  }

  /**
   * Sign out the current Google user.
   */
  async signOut(): Promise<void> {
    this.ensureInitialized();

    try {
      await GoogleSignin.signOut();
    } catch (error) {
      console.error('[GoogleSignInManager] Sign out error:', error);
      throw error;
    }
  }

  /**
   * Revoke access and sign out the user.
   */
  async revokeAccess(): Promise<void> {
    this.ensureInitialized();

    try {
      await GoogleSignin.revokeAccess();
    } catch (error) {
      console.error('[GoogleSignInManager] Revoke access error:', error);
      throw error;
    }
  }

  /**
   * Get the currently signed-in Google user's information.
   */
  async getCurrentUser() {
    this.ensureInitialized();

    try {
      const userInfo = await GoogleSignin.signInSilently();
      return userInfo;
    } catch (error) {
      return null;
    }
  }

  private ensureInitialized(): void {
    if (!this.initialized) {
      throw new Error(
        'GoogleSignInManager not initialized. Call configure() first.',
      );
    }
  }

  private handleError(error: unknown): GoogleSignInResult {
    if (typeof error === 'object' && error !== null && 'code' in error) {
      const code = (error as { code: string }).code;

      switch (code) {
        case statusCodes.SIGN_IN_CANCELLED:
          return {
            success: false,
            error: 'Sign-in was cancelled',
            cancelled: true,
          };
        case statusCodes.IN_PROGRESS:
          return {
            success: false,
            error: 'Sign-in is already in progress',
          };
        case statusCodes.PLAY_SERVICES_NOT_AVAILABLE:
          return {
            success: false,
            error: 'Something went wrong: Google Play Services not available',
          };
        default:
          return {
            success: false,
            error: `Something went wrong: Google Sign-In error: ${code}`,
          };
      }
    }

    const message =
      error instanceof Error ? error.message : 'Unknown error occurred';
    console.error('[GoogleSignInManager] Unexpected error:', error);

    return {
      success: false,
      error: message,
    };
  }
}

export const googleSignInManager = new GoogleSignInManager();
